// types.ts
export enum KYCStatus {
  ONGOING = "ONGOING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  REJECTED = "REJECTED",
}

export enum EnquiryType {
  COMPANY = "Company",
  INDIVIDUAL = "Individual",
}

export interface KYCEntry {
  id: number;
  requestDate: string;
  companyName: string;
  customerName: string;
  customerId: number;
  error?: string;
  status: KYCStatus;
  enquiryType: EnquiryType;
  result: Record<string, unknown> | null;
  comments: Record<string, unknown>[];
  createdAt: string;
  updatedAt: string;
}

export interface KYCResponse {
  data: KYCEntry[];
  total: number;
  page: number;
  limit: number;
}

export interface IDPDocumentOption {
  id: number;
  label: string;
  documentType: string;
  createdAt: string;
}

// Updated to use URLs instead of Files
export interface CompanyKYCFormData {
  customerId: number;
  ktpIds: number[];
  npwpCompanyId: number;
  npwpIndividuIds?: number[];
  ahuUrl?: string;
  nibUrl: string;
  newestAktaUrl: string;
  pendirianAktaUrl?: string;
  passportUrls?: string[];
}

// Rest of types remain the same...
export interface CompanyIdentity {
  name: string;
  npwp: string;
  address: string;
  kode_pos: string;
  no_telp: string;
  email: string;
  nib: string;
  investment_status: string;
}

export interface DeedInfo {
  number: string;
  date: string;
  notary: string;
  location: string;
  ratification_date: string;
  ahu_number: string;
}

export interface ShareInfo {
  amount: number;
  value: string;
  percentage: number;
}

export interface ManagementMember {
  name: string;
  nik: string;
  nik_verified: boolean;
  doc_compared: string | null;
  passport: string | null;
  passport_verified: boolean | null;
  passport_exp: string | null;
  passport_exp_verified: boolean | null;
  birth_place_date: string;
  birth_verified: boolean;
  occupation: string;
  occupation_verified: boolean;
  position: string;
  address: string;
  address_verified: boolean;
  shares: ShareInfo;
  nik_compared: string | null;
  birth_compared: string | null;
  occupation_compared: string | null;
  address_compared: string | null;
  passport_compared: string | null;
  passport_exp_compared: string | null;
}

export interface PastManagement {
  year: string;
  name: string;
  position: string;
  shares: ShareInfo;
  notes: string;
}

export interface BusinessField {
  name: string;
  kbli_code: string;
  notes: string;
}

export interface BusinessFields {
  description: string;
  main_fields: BusinessField[];
}

export interface CapitalChange {
  old_value: string;
  new_value: string;
  description: string;
}

export interface NewShares {
  amount: number;
  value: string;
  description: string;
}

export interface ProfitAllocation {
  purpose: string;
  amount: string;
  percentage: number;
}

export interface ProfitDistribution {
  year: string;
  net_profit: string;
  allocations: ProfitAllocation[];
}

export interface SignificantChanges {
  capital: {
    authorized_capital: CapitalChange;
    paid_up_capital: CapitalChange;
    new_shares: NewShares;
  };
  profit_distribution: ProfitDistribution;
  others: { description: string; sources: string }[];
}

export interface DocumentDiscrepancy {
  type: string;
  description: string;
}

export interface VerifiedResult {
  result: string;
}

export interface CreditHistory {
  creditLimit: number;
  providerCode: string;
  startDate: string;
  dueDate: string;
  usageType: string;
  referenceDate: string;
  status: string;
  debitBalance: number;
  initialCreditLimit: number;
  interestRate: string;
}

export interface CreditInfo {
  tradeName: string;
  customerId: string;
  creditCount: string;
  creditLimit: string;
  debitBalance: string;
  overdue: string;
  statusOverall: string;
  creditHistory: CreditHistory[];
}

export interface PEPNews {
  title: string;
  url: string;
  snippet: string;
}

export interface ManagementPEP {
  name: string;
  is_pep: boolean;
  news?: PEPNews[];
}

export interface NegativeInfo {
  entity: string;
  negative_news: string;
  management: ManagementPEP[];
}

export interface KYCResultData {
  company_identity: CompanyIdentity;
  establishment_deed: DeedInfo;
  latest_amendment: DeedInfo;
  management: ManagementMember[];
  past_management: PastManagement[];
  business_fields: BusinessFields;
  significant_changes: SignificantChanges;
  document_discrepancies: DocumentDiscrepancy[];
  company_summary: string;
  verified_result: VerifiedResult[];
}

export interface ForgeryResult {
  forgery: boolean;
}

export interface KYCComment {
  id: number;
  commenter: string;
  comment: string;
  createdAt: string;
}
